import React from "react"
import PropTypes from "prop-types"
import { Radium, keyframes } from "higher-order-components"
import { BASE_HEADING, BASE_COPY } from "constants/general-style"
import COLOR_PALETTE from "constants/color-palette"
import { Link } from "react-router-dom"
import { AnimationPlayer } from "components"
import { breakpointTablet } from "../settings"
import gtmHandler from "AtomicUtils/gtmHandler"
import classnames from "classnames"
import typographyStyles from "AtomicComponents/Typography/Typography.less"

const RadiumLink = Radium(Link)

const onClickAnimation = {
  base: {
    backgroundPosition: "-100% 0",
    backgroundImage: "linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))",
    backgroundSize: "50% 100%",
    backgroundRepeat: "no-repeat",
    overflow: "hidden"
  },
  animation: {
    animation: "x .666s ease-in reverse forwards",
    animationName: keyframes({
      from: {
        backgroundPosition: "200% 0"
      },
      to: {
        backgroundPosition: "-100% 0"
      }
    })
  }
}

const navigationItemStyle = {
  baseItem: {
    height: "2.5rem",
    padding: "0 1.75em",
    display: "flex",
    alignItems: "center"
  },
  activeLink: {
    color: COLOR_PALETTE.WHITE
  },
  link: {
    fontSize: "13px",
    color: COLOR_PALETTE.GRAY_DARK,
    display: "flex",
    transition: "color .2s",
    textDecoration: "none",
    margin: "1rem 0",
    ":focus": {
      outline: 0
    },
    [breakpointTablet]: {
      ":hover": {
        color: COLOR_PALETTE.WHITE
      }
    }
  },
  line: {
    width: "3px",
    background: COLOR_PALETTE.WHITE,
    transform: "translateX(-100%)",
    opacity: 0
  },
  activeLine: {
    opacity: 1,
    transform: "translateX(0%)"
  }
}

const StyledLink = Radium(({
  fullReload, href, onClick, ...props
}) =>
  (fullReload ?
    <a {...{ ...props, href }} onClick={() => {
      gtmHandler({
        pageCategory: "MyMazda - Home",
        pageAction: `Account - clicked "LOG OUT"`
      })
    }
    }/> :
    <RadiumLink to={href} onClick={onClick} {...props} />
  )
)

export const PrimaryNavigationItem = Radium(({
  active,
  imageUrl,
  label,
  description,
  href,
  fullReload,
  onClick,
  children
}) => (
  <AnimationPlayer
    baseAnimationStyle={onClickAnimation.base} >
    {({ play }) => (
      <StyledLink
        fullReload={fullReload}
        style={{
          ...primaryNavigationItemStyle.link,
          ...active ? primaryNavigationItemStyle.activeLink : {}
        }}
        className={classnames({
          [typographyStyles.withoutStyle]: true
        })}
        onClick={() => {
          play()
          if (onClick) onClick()
        }}
        href={href} >
        <div style={primaryNavigationItemStyle.line} />
        <div style={primaryNavigationItemStyle.base}>
          <div style={{ flex: "1 1 100%" }}>
            <div style={primaryNavigationItemStyle.label}>
              {label}
            </div>
            <div style={primaryNavigationItemStyle.description}>
              {description}
            </div>
          </div>
          {imageUrl && <img src={imageUrl + "?height=80"} alt={label} style={primaryNavigationItemStyle.img} />}
          {children}
        </div>
      </StyledLink>
    )}
  </AnimationPlayer>
))

const primaryNavigationItemStyle = {
  base: {
    ...navigationItemStyle.baseItem,
    // color: COLOR_PALETTE.GRAY_DARK,
    flex: "1 1 100%",
    justifyContent: "space-between"
  },
  activeLink: navigationItemStyle.activeLink,
  line: navigationItemStyle.line,
  activeLine: navigationItemStyle.activeLine,
  link: navigationItemStyle.link,
  img: {
    display: "block",
    height: "100%",
    width: "auto"
  },
  label: {
    ...BASE_HEADING,
    margin: "0.2em 0",
    color: "inherit"
  },
  description: {
    ...BASE_COPY,
    margin: "0.2em 0",
    color: "inherit",
    fontSize: "90%"
  }
}

PrimaryNavigationItem.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func
}

export const SecondaryNavigationItem = Radium(({
  active,
  label,
  href,
  fullReload,
  onClick
}) => (
  <AnimationPlayer
    baseAnimationStyle={onClickAnimation.base}>
    {({ play }) => (
      <StyledLink
        fullReload={fullReload}
        style={{
          ...secondaryNavigationItemStyle.link,
          ...active ? secondaryNavigationItemStyle.activeLink : {}
        }}
        className={classnames({
          [typographyStyles.withoutStyle]: true
        })}
        onClick={() => {
          play()
          if (onClick) onClick()
        }}
        to={href}>
        <div style={[
          secondaryNavigationItemStyle.line,
          active && secondaryNavigationItemStyle.activeLine
        ]} />
        <div style={[
          secondaryNavigationItemStyle.base,
          active && secondaryNavigationItemStyle.activeLink
        ]}>
          <div style={secondaryNavigationItemStyle.label}>
            {label}
          </div>
        </div>
      </StyledLink>
    )}
  </AnimationPlayer>
))

SecondaryNavigationItem.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func
}

const secondaryNavigationItemStyle = {
  link: {
    ...navigationItemStyle.link,
    margin: "0.5rem 0"
  },
  base: {
    ...navigationItemStyle.baseItem,
    color: COLOR_PALETTE.GRAY_DARK
  },
  activeLink: navigationItemStyle.activeLink,
  line: navigationItemStyle.line,
  activeLine: navigationItemStyle.activeLine,
  label: {
    ...BASE_COPY,
    margin: "0.2em 0",
    color: "inherit",
    fontWeight: "600",
    letterSpacing: "0.08em"
  }
}



// WEBPACK FOOTER //
// ./src/containers/mymazda/navigation/navigation-item.jsx